import {
  IonButton,
  IonCard,
  IonCardContent,
  IonChip,
  IonIcon,
  IonSearchbar,
} from '@ionic/react';
import {
  addOutline,
  barbellOutline,
  chevronForwardOutline,
  closeOutline,
} from 'ionicons/icons';
import { collection, getDocs } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Machines } from '../../datamodels';
import { firestore } from '../../firebase';
import {
  getMachineImageUrl,
  MACHINE_FALLBACK_IMAGE,
} from '../../utils/MachineImageUtils';
import './MachineList.css';

type MachineCategory = 'all' | 'cardio';

interface MachineCatalogProps {
  mode?: 'browse' | 'select';
  initialMuscle?: string;
  showCategories?: boolean;
  onSelectMachine?: (machine: Machines) => void;
  onAddMachine?: (machine: Machines) => void;
}

const CARDIO_TERMS = [
  'cardio',
  'elliptical',
  'cross trainer',
  'crosstrainer',
  'treadmill',
  'running machine',
  'exercise bike',
  'stationary bike',
  'indoor bike',
  'cycle',
  'cycling',
  'rower',
  'rowing',
  'stairmaster',
  'stair master',
  'stair climber',
  'stepmill',
];

const isCardioMachine = (machine: Machines) => {
  if (machine.category?.trim().toLowerCase() === 'cardio') return true;

  const searchableText = [
    machine.title,
    ...(machine.exercises || []).map(exercise => exercise.name),
  ]
    .join(' ')
    .toLowerCase();

  return CARDIO_TERMS.some(term => searchableText.includes(term));
};

const MachineCatalog: React.FC<MachineCatalogProps> = ({
  mode = 'browse',
  initialMuscle = '',
  showCategories = true,
  onSelectMachine,
  onAddMachine,
}) => {
  const [machines, setMachines] = useState<Machines[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<MachineCategory>('all');
  const [selectedMuscle, setSelectedMuscle] = useState(initialMuscle);
  const [search, setSearch] = useState('');
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let isMounted = true;
    getDocs(collection(firestore, 'machines')).then(data => {
      if (!isMounted) return;
      setMachines(
        data.docs.map(machineDoc => ({
          ...machineDoc.data(),
          id: machineDoc.id,
        })) as Machines[]
      );
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setSelectedMuscle(initialMuscle);
  }, [initialMuscle]);

  const muscleOptions = useMemo(
    () =>
      Array.from(
        new Set(
          machines.flatMap(machine =>
            (machine.exercises || []).flatMap(
              exercise => exercise.muscles || []
            )
          )
        )
      ).sort(),
    [machines]
  );

  const filteredMachines = useMemo(() => {
    const term = search.trim().toLowerCase();
    return machines
      .filter(machine =>
        selectedCategory === 'cardio' ? isCardioMachine(machine) : true
      )
      .filter(machine =>
        selectedMuscle
          ? machine.exercises?.some(exercise =>
              exercise.muscles?.includes(selectedMuscle)
            )
          : true
      )
      .filter(machine =>
        term
          ? machine.title.toLowerCase().includes(term) ||
            machine.exercises?.some(
              exercise =>
                exercise.name.toLowerCase().includes(term) ||
                exercise.muscles?.some(muscle =>
                  muscle.toLowerCase().includes(term)
                )
            )
          : true
      )
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [machines, search, selectedCategory, selectedMuscle]);

  return (
    <div className={`machine-catalog machine-catalog--${mode}`}>
      <IonSearchbar
        className="machine-search"
        value={search}
        debounce={150}
        placeholder="Search machines, exercises or muscles"
        onIonInput={event => setSearch(event.detail.value || '')}
      />

      {showCategories && (
        <>
          <div className="machine-filter-label">Categories</div>
          <div className="machine-category-filters" aria-label="Filter by machine category">
            <IonChip
              className={selectedCategory === 'all' ? 'is-selected' : ''}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </IonChip>
            <IonChip
              className={selectedCategory === 'cardio' ? 'is-selected' : ''}
              onClick={() =>
                setSelectedCategory(current =>
                  current === 'cardio' ? 'all' : 'cardio'
                )
              }
            >
              Cardio
              {selectedCategory === 'cardio' && <IonIcon icon={closeOutline} />}
            </IonChip>
          </div>
        </>
      )}

      <div className="machine-filter-label">Muscle groups</div>
      <div className="machine-muscle-filters" aria-label="Filter by muscle">
        <IonChip
          className={!selectedMuscle ? 'is-selected' : ''}
          onClick={() => setSelectedMuscle('')}
        >
          All
        </IonChip>
        {muscleOptions.map(muscle => (
          <IonChip
            key={muscle}
            className={selectedMuscle === muscle ? 'is-selected' : ''}
            onClick={() =>
              setSelectedMuscle(current => (current === muscle ? '' : muscle))
            }
          >
            {muscle}
            {selectedMuscle === muscle && <IonIcon icon={closeOutline} />}
          </IonChip>
        ))}
      </div>

      <div className="machine-list-heading">
        <h2>
          {selectedCategory === 'cardio'
            ? selectedMuscle
              ? `Cardio · ${selectedMuscle}`
              : 'Cardio machines'
            : selectedMuscle || 'All machines'}
        </h2>
        <span>{filteredMachines.length} found</span>
      </div>

      <div className="machine-card-list">
        {filteredMachines.map(machine => {
          const imageUrl = getMachineImageUrl(machine);
          const muscles = Array.from(
            new Set(
              (machine.exercises || []).flatMap(
                exercise => exercise.muscles || []
              )
            )
          );
          const isSelectMode = mode === 'select';

          return (
            <IonCard
              button
              className="machine-list-card"
              key={machine.id}
              routerLink={isSelectMode ? undefined : `/my/machines/${machine.id}`}
              onClick={
                isSelectMode ? () => onSelectMachine?.(machine) : undefined
              }
            >
              <IonCardContent>
                <div className="machine-list-card__visual">
                  {imageUrl && !brokenImages[machine.id] ? (
                    <img
                      src={imageUrl}
                      alt={machine.title}
                      onError={() =>
                        setBrokenImages(current => ({
                          ...current,
                          [machine.id]: true,
                        }))
                      }
                    />
                  ) : (
                    <img
                      src={MACHINE_FALLBACK_IMAGE}
                      alt=""
                      className="is-fallback"
                    />
                  )}
                </div>
                <div className="machine-list-card__copy">
                  <h3>{machine.title}</h3>
                  <p>
                    {machine.exercises?.length || 0}{' '}
                    {machine.exercises?.length === 1 ? 'exercise' : 'exercises'}
                  </p>
                  <div className="machine-list-card__muscles">
                    {muscles.slice(0, 3).map(muscle => (
                      <span key={muscle}>{muscle}</span>
                    ))}
                  </div>
                </div>
                {isSelectMode ? (
                  <IonIcon
                    icon={addOutline}
                    className="machine-list-card__select"
                    aria-hidden="true"
                  />
                ) : onAddMachine ? (
                  <IonButton
                    className="machine-list-card__add"
                    fill="solid"
                    aria-label={`Add ${machine.title} to workout`}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onAddMachine(machine);
                    }}
                  >
                    <IonIcon icon={addOutline} slot="icon-only" />
                  </IonButton>
                ) : (
                  <IonIcon
                    icon={chevronForwardOutline}
                    className="machine-list-card__arrow"
                    aria-hidden="true"
                  />
                )}
              </IonCardContent>
            </IonCard>
          );
        })}
      </div>

      {!filteredMachines.length && (
        <div className="machine-list-empty">
          <IonIcon icon={barbellOutline} />
          <strong>No machines found</strong>
          <span>Try another search or muscle filter.</span>
        </div>
      )}
    </div>
  );
};

export default MachineCatalog;
